/**
 * Website audit buyers land here from their sign-in link (`/integrations?audit=<entitlement>&tier=&website=`, billing
 * audit-portal.ts). The banner explains the read-only connection and links to the connected audit (/audit/new) with
 * that entitlement and the order's website. The parameters are kept for this tab session, because the provider consent
 * round-trip returns to plain /integrations.
 */
import { Link } from 'react-router';
import { Heading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';

export interface AuditConnect { entitlement: string; tier: string; website: string | null }
const KEY = 'qm.audit.connect';
const ID = /^[A-Za-z0-9_-]{4,80}$/;
const TIERS = new Set(['pro', 'professional', 'enterprise']);

/** Read from the URL (and remember it), else from this tab's session; null when absent or malformed. */
export function auditConnectFrom(params: URLSearchParams, storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSession()): AuditConnect | null {
  const parse = (entitlement: string | null, tier: string | null, website: string | null): AuditConnect | null => {
    if (!entitlement || !ID.test(entitlement) || !tier || !TIERS.has(tier)) return null;
    let site: string | null;
    try { site = website && new URL(website).protocol.startsWith('http') ? website : null; } catch { site = null; }
    return { entitlement, tier, website: site };
  };
  const fromUrl = parse(params.get('audit'), params.get('tier'), params.get('website'));
  if (fromUrl) {
    try { storage?.setItem(KEY, JSON.stringify(fromUrl)); } catch { /* storage blocked */ }
    return fromUrl;
  }
  try {
    const raw = storage?.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Partial<AuditConnect>) : null;
    return v ? parse(v.entitlement ?? null, v.tier ?? null, v.website ?? null) : null;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
}

export function auditJobLink(a: AuditConnect): string {
  const q = new URLSearchParams({ entitlement: a.entitlement, tier: a.tier });
  if (a.website) q.set('website', a.website);
  return `/audit/new?${q.toString()}`;
}

export function AuditConnectBanner({ audit }: { audit: AuditConnect }) {
  const { t } = useApp();
  return (
    <Panel aria-labelledby="audit-connect-title">
      <Heading level={2} id="audit-connect-title">{t('integrations.auditConnect.title')}</Heading>
      <Text>{t('integrations.auditConnect.body')}</Text>
      <Text tone="muted">{t('integrations.auditConnect.wait')}</Text>
      <Link to={auditJobLink(audit)} className="qm-button" data-variant="primary">{t('integrations.auditConnect.cta')}</Link>
    </Panel>
  );
}
